import { Component, ElementRef, OnInit, ViewChild, input, model } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { map, Observable, of, startWith, Subject } from 'rxjs';
import { ConfirmationToastComponent } from 'src/app/components/confirmation-toast/confirmation-toast.component';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-edit-skills',
  templateUrl: './edit-skills.component.html',
  styleUrl: './edit-skills.component.scss',
  standalone: false
})
export class EditSkillsComponent implements OnInit {
  edit_skills = model<boolean>();
  user = input<any>();
  refreshUser = input<Subject<void>>();
  exp_levels: any;
  gaccList: any = [];
  filtered_gaccList!: Observable<any[]>;
  isAdmin: boolean = false;
  @ViewChild('gacc') input?: ElementRef<HTMLInputElement>;

  skillsForm = this.formBuilder.group({
    'Team Leader': ["-1"],
    'Archaeology': ["-1"],
    'Engineer': ["-1"],
    'Fisheries': ["-1"],
    'Geology': ["-1"],
    'GIS': ["-1"],
    'Hydrologist': ["-1"],
    'Interagency Coordinator': ["-1"],
    'Invasive Species/Botany': ["-1"],
    'Public Information Officer': ["-1"],
    'Recreation Specialist': ["-1"],
    'Soil Scientist': ["-1"],
    'Wildlife Biologist': ["-1"],
    'Implementation': ["-1"],
    'Resource Advisor (READ)': ["-1"],
    'Resource Advisor - Fireline (REAF)': ["-1"],
    'Resource Advisor - Lead (REAC)': ["-1"],
    'Resource Advisor - Archaeologist (ARCH)': ["-1"],
    skills_text: [""],
    large_fire_exp: [false],
    red_card_qualifications: [""],
    team_references: [""],
    skills_add_comments: [""],
    GACC_dispatch_center_id: ["-1"],
    IQCS_employee_id: [""],
    read_text: [""],
    BAS: [false],
    BADO: [false],
    BAEL: [false],
    archaeology_heritage_tribal: [false],
    botany_invasives: [false],
    lead_read: [false],
    roads_engineering: [false],
    suppression_repair: [false],
    timber: [false],
    trails_recreation: [false],
    watershed_fisheries_ais: [false],
    wilderness: [false],
    wildlife: [false],
    nis_baer: [false],
    vgis: [false],
  });

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly userService: UserService,
    private readonly toast: ConfirmationToastComponent,
  ) {
    this.exp_levels = this.userService.getExpList();
    this.setUser();
  }

  ngOnInit(): void {
    this.userService.getGACCList().subscribe((gaccList) => {
      this.gaccList = gaccList;

      this.filtered_gaccList = this.skillsForm.controls["GACC_dispatch_center_id"].valueChanges.pipe(
        startWith(""),
        map((value) => this._filter((value) ?? ""))
      )
      this.skillsForm.patchValue({
        GACC_dispatch_center_id: gaccList.find((gaccItem: any) => gaccItem.value == this.user().GACC_dispatch_center_id) ?? '',
      })
    })
    let skillIdMap = this.userService.getSkillIdMap()

    let values: { [id: string]: any } = {};
    this.user().skills.forEach((skillObject: any) => {
      values[skillIdMap.get(skillObject.skill_id)!] = skillObject.experience_id;
    })
    this.skillsForm.patchValue(values) // Patch skill values from user object
    this.skillsForm.patchValue({
      skills_text: this.user().skills_text,
      large_fire_exp: this.user().large_fire_exp,
      red_card_qualifications: this.user().red_card_qualifications,
      team_references: this.user().team_references,
      skills_add_comments: this.user().skills_add_comments,
      IQCS_employee_id: this.user().IQCS_employee_id,
      read_text: this.user().read_text,
      BAS: this.user().BAS,
      BADO: this.user().BADO,
      BAEL: this.user().BAEL,
      archaeology_heritage_tribal: this.user().archaeology_heritage_tribal,
      botany_invasives: this.user().botany_invasives,
      lead_read: this.user().lead_read,
      roads_engineering: this.user().roads_engineering,
      suppression_repair: this.user().suppression_repair,
      timber: this.user().timber,
      trails_recreation: this.user().trails_recreation,
      watershed_fisheries_ais: this.user().watershed_fisheries_ais,
      wilderness: this.user().wilderness,
      wildlife: this.user().wildlife,
      nis_baer: this.user().NIS_BAER,
      vgis: this.user().VGIS,
    })
  }

  setUser() {
    this.userService.getUserProfile().then((user) => {
      if (user.isWO) this.isAdmin = true;
    })
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase()
    return this.gaccList.filter((option: any) =>
      option.text.toLowerCase().includes(filterValue) ?? option.description.toLowerCase().includes(filterValue)
    )
  }

  filter(): void {
    const filterValue = this.input?.nativeElement.value.toLowerCase() ?? '';
    this.filtered_gaccList = of(this._filter(filterValue));
  }

  displayFn(gacc?: any) {
    if (gacc)
      return gacc.text + ' - ' + gacc.description;
    else
      return '';
  }

  save() {
    let skills = []
    let skillNames = this.userService.getSkillNames()

    // Check each item in skillForm for a value to save to skills
    for (let skill in this.skillsForm.value) {
      if (skill in skillNames) {
        if (this.skillsForm.get(skill)!.value != -1) {
          skills.push({
            skill_id: skillNames[skill],
            experience_id: this.skillsForm.get(skill)!.value
          })
        }
      }
    }

    let gacc: any = this.skillsForm.value.GACC_dispatch_center_id;

    this.userService.editUserDetails({
      user_id: this.user().user_id,
      skills_list: skills,
      skills_text: this.skillsForm.value.skills_text,
      red_card_qualifications: this.skillsForm.value.red_card_qualifications,
      large_fire_exp: this.skillsForm.value.large_fire_exp,
      team_references: this.skillsForm.value.team_references,
      skills_add_comments: this.skillsForm.value.skills_add_comments,
      GACC_dispatch_center_id: gacc?.value,
      IQCS_employee_id: this.skillsForm.value.IQCS_employee_id,
      read_text: this.skillsForm.value.read_text,
      BAS: this.skillsForm.value.BAS,
      BADO: this.skillsForm.value.BADO,
      BAEL: this.skillsForm.value.BAEL,
      archaeology_heritage_tribal: this.skillsForm.value.archaeology_heritage_tribal,
      botany_invasives: this.skillsForm.value.botany_invasives,
      lead_read: this.skillsForm.value.lead_read,
      roads_engineering: this.skillsForm.value.roads_engineering,
      suppression_repair: this.skillsForm.value.suppression_repair,
      timber: this.skillsForm.value.timber,
      trails_recreation: this.skillsForm.value.trails_recreation,
      watershed_fisheries_ais: this.skillsForm.value.watershed_fisheries_ais,
      wilderness: this.skillsForm.value.wilderness,
      wildlife: this.skillsForm.value.wildlife,
      NIS_BAER: this.skillsForm.value.nis_baer,
      VGIS: this.skillsForm.value.vgis,
    }).subscribe({
      next: (response) => {
        this.toast.openSuccessSnackBar(`User's skills were successfully updated.`);
        this.edit_skills.update((x) => {
          return false;
        });
        this.refreshUser()!.next();
      },
      error: (response) => {
        this.toast.openErrorSnackBar("There was an error updating the user.", "Please try again.");
      },
    })
  }

  cancel() {
    this.edit_skills.update((x) => {
      return false;
    });
  }
}
